import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-background-50 shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`ri-file-list-3-line text-xl ${scrolled ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <span className={`font-heading text-lg font-semibold whitespace-nowrap transition-colors duration-300 ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>
              Sugargoo Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'}`}
            >
              Home
            </Link>
            <Link
              to="/article/sugargoo-spreadsheet-beginner-tutorial"
              className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'}`}
            >
              Shopping Guide
            </Link>
            <a href="#categories" className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'}`}>
              Categories
            </a>
            <a href="#reviews" className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'}`}>
              Reviews
            </a>
            <a href="#shipping" className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'}`}>
              Shipping
            </a>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${scrolled ? 'bg-primary-500 text-background-50 hover:bg-primary-600' : 'bg-background-50/20 text-background-50 hover:bg-background-50/30 backdrop-blur-sm'}`}
              >
                Browse Products
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
                </div>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-200 cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="md:hidden flex items-center" ref={mobileRef}>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${scrolled ? 'text-foreground-950 bg-background-100' : 'text-background-50 bg-background-50/20'}`}
              aria-label="Toggle menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            <Link to="/" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </div>
              Home
            </Link>
            <Link to="/article/sugargoo-spreadsheet-beginner-tutorial" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </div>
              Shopping Guide
            </Link>
            <a href="#categories" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-grid-line"></i>
              </div>
              Categories
            </a>
            <a href="#reviews" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-star-line"></i>
              </div>
              Reviews
            </a>
            <a href="#shipping" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-ship-line"></i>
              </div>
              Shipping
            </a>
          </div>

          <div className="border-t border-background-200 mt-2 pt-3 pb-4">
            <p className="px-6 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Product Categories</p>
            <div className="grid grid-cols-2 gap-1 px-4">
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center gap-2 px-2 py-2.5 rounded-lg text-xs font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${getMobileCatIcon(cat.seq)} text-sm text-foreground-400`}></i>
                  </div>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-background-200 pt-4 pb-6 px-6">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Visit Main Website
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line"></i>
              </div>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

function getMobileCatIcon(seq: string): string {
  const iconMap: Record<string, string> = {
    'cat-shoes': 'ri-walk-line',
    'cat-hoodies': 'ri-shirt-line',
    'cat-tshirts': 'ri-shirt-line',
    'cat-jackets': 'ri-vip-crown-line',
    'cat-pants': 'ri-archive-line',
    'cat-headwear': 'ri-sun-line',
    'cat-sets': 'ri-stack-line',
    'cat-underwear': 'ri-user-heart-line',
    'cat-jersey': 'ri-trophy-line',
    'cat-accessories': 'ri-star-line',
    'cat-bags': 'ri-handbag-line',
    'cat-belts': 'ri-link-m',
    'cat-perfumes': 'ri-flask-line',
    'cat-other': 'ri-more-line',
  };
  return iconMap[seq] || 'ri-price-tag-3-line';
}